import {
  Button,
  Checkbox,
  Drawer,
  Image,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  productWantRequestMatches,
  productWantRequestSendEmail,
} from 'apis/product-want-request';
import {
  findLabelByValue,
  PMS_PRODUCT_STOCK_STATUS,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
} from 'commons/options';
import LOCALS from 'commons/locals';
import showTotal from 'components/show-total';
import {
  PUBLISH_STATUS_MAP,
  PUBLISH_STATUS_OPTION_LIST,
} from 'constant/pms-product';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { PmsProductWantRequestMatchItem } from 'types/pms-product-want-request';
import { getMaterialOptionsForCategory } from './common';
import findCascaderOptionById from 'utils/findCascaderOptionById';

type Props = {
  open: boolean;
  requestId?: number;
  customerEmail?: string;
  onClose: () => void;
  onEmailSent?: () => void;
};

const MatchDrawer = ({
  open,
  requestId,
  customerEmail,
  onClose,
  onEmailSent,
}: Props) => {
  const { t } = useTranslation();
  const {
    rankSelectOptions,
    colorSelectOptions,
    hardwareSelectOptions,
    stampSelectOptions,
    productCategoryCascaderOptions,
    materialCascaderOptionsMap,
  } = useAppSelector(selectGlobalInfo);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [list, setList] = useState<PmsProductWantRequestMatchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [forSaleOnly, setForSaleOnly] = useState(false);

  const loadData = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const { list: matchList, total: matchTotal } =
        await productWantRequestMatches(requestId, {
          pageNum,
          pageSize,
          stockStatus: forSaleOnly
            ? PMS_PRODUCT_STOCK_STATUS.FOR_SALE
            : undefined,
        });
      setList(matchList);
      setTotal(matchTotal);
    } finally {
      setLoading(false);
    }
  }, [requestId, pageNum, pageSize, forSaleOnly]);

  useEffect(() => {
    if (open && requestId) {
      loadData();
    }
  }, [open, requestId, pageNum, pageSize, forSaleOnly, loadData]);

  useEffect(() => {
    if (!open) {
      setPageNum(1);
      setPageSize(10);
      setList([]);
      setTotal(0);
      setSelectedProductIds([]);
      setForSaleOnly(false);
    }
  }, [open]);

  const handleSendEmail = () => {
    if (!requestId) return;
    if (!customerEmail) {
      message.error(t(LOCALS.pwr_send_email_no_email));
      return;
    }
    if (!selectedProductIds.length) {
      message.warning(t(LOCALS.pwr_send_email_select_products));
      return;
    }

    Modal.confirm({
      title: t(LOCALS.pwr_send_email_confirm_title),
      content: t(LOCALS.pwr_send_email_confirm_content, {
        email: customerEmail,
        count: selectedProductIds.length,
      }),
      okText: t(LOCALS.confirm),
      cancelText: t(LOCALS.cancel),
      onOk: async () => {
        setSending(true);
        try {
          await productWantRequestSendEmail(requestId, {
            productIds: selectedProductIds,
          });
          message.success(t(LOCALS.pwr_send_email_success));
          setSelectedProductIds([]);
          onEmailSent?.();
        } finally {
          setSending(false);
        }
      },
    });
  };

  const columns: ColumnsType<PmsProductWantRequestMatchItem> = useMemo(
    () => [
      {
        title: <Trans i18nKey={LOCALS.pwr_match_score} />,
        dataIndex: 'matchScore',
        width: 80,
        render: (score: number) => <Tag color="blue">{score}</Tag>,
      },
      {
        title: <Trans i18nKey={LOCALS.product} />,
        width: 280,
        render: (_, row) => (
          <div className="flex gap-2">
            {row.product.pic ? (
              <Image src={row.product.pic} width={48} height={48} />
            ) : null}
            <div>
              <Link to={`/pms/product-edit/${row.product.id}`} target="_blank">
                {row.product.name}
              </Link>
              <div className="text-gray-500 text-xs">ID: {row.product.id}</div>
            </div>
          </div>
        ),
      },
      {
        title: <Trans i18nKey={LOCALS.rank} />,
        width: 100,
        render: (_, row) => {
          const option = rankSelectOptions.find(
            (d) => d.value === row.product.rank,
          );
          return option?.label || row.product.rank || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.color} />,
        width: 120,
        render: (_, row) => {
          const colorCodes = (row.product.color ?? '')
            .split(',')
            .filter(Boolean);
          if (!colorCodes.length) return '-';
          return colorCodes
            .map((code) => {
              const option = colorSelectOptions.find((d) => d.value === code);
              return option?.label || code;
            })
            .join(', ');
        },
      },
      {
        title: <Trans i18nKey={LOCALS.hardware} />,
        width: 120,
        render: (_, row) => {
          const codes = (row.product.hardware ?? '').split(',').filter(Boolean);
          if (!codes.length) return '-';
          return codes
            .map((code) => {
              const option = hardwareSelectOptions.find(
                (d) => d.value === code,
              );
              return option?.label || code;
            })
            .join(', ');
        },
      },
      {
        title: <Trans i18nKey={LOCALS.stamp} />,
        width: 100,
        render: (_, row) => {
          const code = row.product.stamp;
          if (!code) return '-';
          return (
            stampSelectOptions.find((d) => d.value === code)?.label || code
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.pwr_stock_status} />,
        width: 100,
        render: (_, row) =>
          findLabelByValue(
            row.product.stockStatus,
            PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
          ),
      },
      {
        title: <Trans i18nKey={LOCALS.publish_status} />,
        width: 90,
        render: (_, row) => {
          const isOnShelf =
            row.product.publishStatus === PUBLISH_STATUS_MAP.ON_SHELF;
          return (
            <Tag color={isOnShelf ? 'success' : 'default'}>
              {findLabelByValue(
                row.product.publishStatus,
                PUBLISH_STATUS_OPTION_LIST,
              )}
            </Tag>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.pwr_match_hits} />,
        render: (_, row) => {
          const d = row.matchDetails;
          const mateList = getMaterialOptionsForCategory(
            row.product.productCategoryId,
            productCategoryCascaderOptions,
            materialCascaderOptionsMap,
          );
          return (
            <>
              {d.categoryMatched && (
                <Tag>
                  <Trans i18nKey={LOCALS.pwr_hit_category} />
                </Tag>
              )}
              {d.rankMatched && (
                <Tag>
                  <Trans i18nKey={LOCALS.pwr_hit_rank} />
                </Tag>
              )}
              {d.colorMatched &&
                (d.matchedColors ?? []).map((code) => {
                  const label =
                    colorSelectOptions.find((opt) => opt.value === code)
                      ?.label || code;
                  return <Tag key={`color-${code}`}>{label}</Tag>;
                })}
              {d.hardwareMatched &&
                (d.matchedHardware ?? []).map((code) => {
                  const label =
                    hardwareSelectOptions.find((opt) => opt.value === code)
                      ?.label || code;
                  return <Tag key={`hardware-${code}`}>{label}</Tag>;
                })}
              {d.stampMatched &&
                (d.matchedStamps ?? []).map((code) => {
                  const label =
                    stampSelectOptions.find((opt) => opt.value === code)
                      ?.label || code;
                  return <Tag key={`stamp-${code}`}>{label}</Tag>;
                })}
              {(d.matchedMaterials ?? []).map((code) => {
                const label =
                  findCascaderOptionById(code, mateList)?.label || code;
                return <Tag key={code}>{label}</Tag>;
              })}
            </>
          );
        },
      },
    ],
    [
      rankSelectOptions,
      colorSelectOptions,
      hardwareSelectOptions,
      stampSelectOptions,
      productCategoryCascaderOptions,
      materialCascaderOptionsMap,
    ],
  );

  return (
    <Drawer
      title={<Trans i18nKey={LOCALS.pwr_match_drawer_title} />}
      open={open}
      onClose={onClose}
      width={960}
      destroyOnClose
      extra={
        <Space>
          {customerEmail ? (
            <span className="text-sm text-gray-500">{customerEmail}</span>
          ) : null}
          <Button
            type="primary"
            loading={sending}
            disabled={!selectedProductIds.length}
            onClick={handleSendEmail}
          >
            <Trans i18nKey={LOCALS.pwr_send_email} />
          </Button>
        </Space>
      }
    >
      <div className="mb-4">
        <Checkbox
          checked={forSaleOnly}
          onChange={(e) => {
            setForSaleOnly(e.target.checked);
            setPageNum(1);
            setSelectedProductIds([]);
          }}
        >
          <Trans i18nKey={LOCALS.pwr_match_for_sale_only} />
        </Checkbox>
      </div>
      <Table
        bordered
        rowKey="productId"
        loading={loading}
        columns={columns}
        dataSource={list}
        rowSelection={{
          selectedRowKeys: selectedProductIds,
          onChange: (keys) => {
            setSelectedProductIds(keys as number[]);
          },
          getCheckboxProps: (row) => ({
            disabled: !row.emailEligible,
          }),
          renderCell: (_checked, row, _index, originNode) =>
            row.emailEligible ? (
              originNode
            ) : (
              <Tooltip title={t(LOCALS.pwr_send_email_ineligible_tip)}>
                {originNode}
              </Tooltip>
            ),
        }}
        pagination={{
          showTotal,
          total,
          current: pageNum,
          pageSize,
          onChange: (page, size) => {
            setPageNum(page);
            setPageSize(size);
          },
        }}
      />
    </Drawer>
  );
};

export default MatchDrawer;
